import type { RelatedItem } from './types';
import type { Notifications } from 'types/api-responses';
import type { VestingContext } from 'components/vesting-documents/types';
import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';
import request from 'lib/request';

// Host-agnostic data layer: request URLs come from `host.basePath`. No shared list
// atom — each section owns its list state — so only stateless mutation atoms live here.

/** A mount host for the Related Items section — anything with a REST base path. */
export interface RelatedItemsHost {
  /** e.g. `/documents/123` (Document) or `/wells/<uwi>` (Well, keyed by UWI). */
  basePath: string;
  /** The host's Pusher block, so the list can refresh itself when another tab
   * (or another user) changes a link. Omit to opt out of live updates. */
  notifications?: Notifications;
  /** The CRM tract's Salesforce Id — external title support is routed under
   * `/crm/tracts/:id`, so only that host can upload or link a workbook. */
  tractId?: string;
  /** Set when a Title Support Workspace hosts the panel: the workbook picker then offers what the workspace lacks. */
  titleSupportWorkspaceId?: number;
  /** The tract's search context; only a host that carries it offers Vesting Document. */
  vesting?: VestingContext;
}

/** Pusher event the backend fires on every link create / edit / remove. */
export const RELATED_ITEMS_CHANGED_EVENT = 'change-related-items';

export const relatedItemsUrl = (host: RelatedItemsHost) =>
  `${host.basePath}/related_items`;

export const relatedItemUrl = (host: RelatedItemsHost, id: number | string) =>
  `${relatedItemsUrl(host)}/${id}`;

// External title support keeps its own controller. One workbook is filed against
// many tracts, so it is not a related_items record despite rendering as a row here.
export const externalTitleSupportsUrl = (host: RelatedItemsHost) =>
  `${host.basePath}/external_title_supports`;

// Signed id goes at the top level (`attachment`), not nested under a record key.
export const externalTitleSupportUploadUrl = (host: RelatedItemsHost) =>
  `${externalTitleSupportsUrl(host)}/upload`;

// Upload + provision a document and link the uploaded document in one step (POST).
export const relatedItemProvisionUrl = (host: RelatedItemsHost) =>
  `${relatedItemsUrl(host)}/provision`;

/** What a row's action URLs are built from: its id, plus its own path when served. */
export type RelatedItemRef = Pick<RelatedItem, 'id' | 'href'>;

// A row's REST path, which is not always the host's: a Well Workspace's list reads
// through to the canonical well, and those rows are served with the well route. Falls
// back to the host path for a backend that sends no per-row href.
export const relatedItemPath = (host: RelatedItemsHost, item: RelatedItemRef) =>
  item.href || relatedItemUrl(host, item.id);

// Where a row's writes (edit, unlink) go. Reads and downloads follow the row's own
// `href`, but a write stays on the host — a Well Workspace stages the removal of a
// canonical row rather than deleting it at the well. A workbook is the one exception:
// it is not a related_items record at all, so only its own controller can unlink it.
export const relatedItemWriteUrl = (
  host: RelatedItemsHost,
  item: RelatedItem
) => {
  if (item.kind === 'external_title_support' && item.href) return item.href;

  return relatedItemUrl(host, item.id);
};

// Download link (GET): backend 302s to the blob and records nothing, so a plain anchor
// (and any prefetch/unfurl) is safe. The audit event is logged separately by logRelatedItemDownload.
export const relatedItemDownloadUrl = (
  host: RelatedItemsHost,
  item: RelatedItemRef
) => `${relatedItemPath(host, item)}/download`;

// Audit endpoint (POST) hit on a real download click.
export const relatedItemLogDownloadUrl = (
  host: RelatedItemsHost,
  item: RelatedItemRef
) => `${relatedItemPath(host, item)}/log_download`;

// Best-effort audit ping on a real Download click. A POST (not the GET anchor), so a
// prefetch never logs a phantom download; fire-and-forget so a failed ping can't block it.
export const logRelatedItemDownload = (
  host: RelatedItemsHost,
  item: RelatedItemRef
): void => {
  void request.post(
    relatedItemLogDownloadUrl(host, item),
    {},
    new AbortController(),
    () => {}
  );
};

export type RelatedItemsSortDirection = 'ASC' | 'DESC';

export interface RelatedItemsListParams {
  page: number;
  pageSize: number;
  text?: string;
  /** One of the backend's allowlisted columns; anything else falls back to its default. */
  sortAttribute?: string;
  sortDirection?: RelatedItemsSortDirection;
}

// Server-driven list URL: page / page_size / text / sort drive the paginated
// envelope. Query keys stay snake_case — the backend reads them as sent.
export const relatedItemsListUrl = (
  host: RelatedItemsHost,
  params: RelatedItemsListParams
): string => {
  const query = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.pageSize),
  });
  const text = params.text?.trim();
  if (text) query.set('text', text);
  if (params.sortAttribute) {
    query.set('sort_attribute', params.sortAttribute);
    query.set('sort_direction', params.sortDirection ?? 'ASC');
  }
  return `${relatedItemsUrl(host)}?${query.toString()}`;
};

// Create a related item (POST): an uploaded file (DirectUpload signed_id), a
// note, an external link, or a record link.
export const addRelatedItemAtom = buildSectionMutationAtom<RelatedItem>();

// Edit a related item in place (PATCH .../related_items/:id): a file's
// name/notes, a note's body, or a link row's url/label.
export const updateRelatedItemAtom = buildSectionMutationAtom<RelatedItem>();

// Upload + provision a document and link it (POST .../related_items/provision).
export const provisionRelatedItemAtom = buildSectionMutationAtom<RelatedItem>();

/** Delete a related item (DELETE .../related_items/:id). */
export const deleteRelatedItemAtom = buildSectionMutationAtom<unknown>();
